import { FlexibleSecureContextManager } from './FlexibleSecureContextManager';
import { ConversationManager } from './ConversationManager';

// Adaptive Audio Manager with Voice + Text Fallback
export class AdaptiveAudioManager {
  private conversationManager: ConversationManager;
  private voiceCapabilities: any;
  private recognition: SpeechRecognition | null = null;
  private synthesis: SpeechSynthesis;
  private isListening: boolean = false;
  private isTextMode: boolean = false;
  private onStatusChange?: (status: string) => void;

  constructor() {
    this.conversationManager = new ConversationManager();
    this.synthesis = window.speechSynthesis;
    this.voiceCapabilities = FlexibleSecureContextManager.checkVoiceAvailability();
  }

  setCallbacks(onStatusChange: (status: string) => void) {
    this.onStatusChange = onStatusChange;
  }

  async initialize(): Promise<void> {
    console.log('🚀 Initializing Adaptive Audio Manager...');
    console.log('Voice capabilities:', this.voiceCapabilities);

    if (this.voiceCapabilities.canUseVoice) {
      // Try to initialize voice features
      try {
        await this.initializeVoiceMode();
        console.log('✅ Voice mode activated');
        this.updateStatus('listening');
      } catch (error) {
        console.warn('⚠️ Voice initialization failed, falling back to text mode:', error);
        this.initializeTextMode();
      }
    } else {
      console.log('💬 Voice not available, using text mode');
      this.initializeTextMode();
    }
  }

  private async initializeVoiceMode(): Promise<void> {
    // Request microphone permission
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });

    // Initialize speech recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    this.recognition!.continuous = true;
    this.recognition!.interimResults = true;
    this.recognition!.lang = 'en-US';

    this.setupSpeechHandlers();
    
    // Clean up stream (we just needed permission)
    stream.getTracks().forEach(track => track.stop());
    
    this.isTextMode = false;
  }

  private initializeTextMode(): void {
    this.isTextMode = true;
    this.setupTextInterface();
    this.updateStatus('text-mode');
  }

  private setupSpeechHandlers(): void {
    if (!this.recognition) return;

    this.recognition.onstart = () => {
      console.log('🎤 Listening...');
      this.isListening = true;
      this.updateStatus('listening');
    };

    this.recognition.onresult = (event: any) => {
      const lastResult = event.results[event.results.length - 1];
      if (lastResult.isFinal) {
        const transcript = lastResult[0].transcript.trim();
        this.processUserInput(transcript);
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('❌ Speech error:', event.error);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        console.log('🔄 Switching to text mode due to permission issues');
        this.initializeTextMode();
      }
    };

    this.recognition.onend = () => {
      this.isListening = false;
      if (!this.isTextMode) {
        setTimeout(() => this.startListening(), 1000);
      }
    };
  }

  private setupTextInterface(): void {
    // Create floating text input
    this.createTextInputInterface();
  }

  private createTextInputInterface(): void {
    // Remove existing interface if any
    const existingInterface = document.getElementById('avatar-text-interface');
    if (existingInterface) {
      existingInterface.remove();
    }

    const textInterface = document.createElement('div');
    textInterface.id = 'avatar-text-interface';
    textInterface.innerHTML = `
      <div style="
        position: fixed;
        bottom: 100px;
        right: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        border-radius: 15px;
        box-shadow-subtle: 0 8px 32px rgba(0,0,0,0.3);
        z-index: 1000;
        min-width: 350px;
        backdrop-filter: blur(10px);
      ">
        <div style="margin-bottom: 10px; font-weight: bold;">
          💬 Chat with RICHGURU AI
        </div>
        
        <div style="font-size: 0.9em; margin-bottom: 15px; opacity: 0.8;">
          ${FlexibleSecureContextManager.getAccessInstructions()}
        </div>
        
        <div style="display: flex; gap: 10px;">
          <input 
            type="text" 
            id="avatar-text-input"
            placeholder="Ask me about your portfolio, market analysis..."
            style="
              flex: 1;
              padding: 12px;
              border: none;
              border-radius: 25px;
              outline: none;
              background: rgba(255,255,255,0.2);
              color: white;
            "
          />
          <button 
            id="avatar-text-send"
            style="
              padding: 12px 20px;
              border: none;
              border-radius: 25px;
              background: rgba(255,255,255,0.3);
              color: white;
              cursor: pointer;
              font-weight: bold;
            "
          >
            Send
          </button>
        </div>
        
        <div id="quick-actions" style="margin-top: 15px; display: flex; gap: 8px; flex-wrap: wrap;">
          <button class="quick-action" data-message="What's my portfolio balance?">Portfolio</button>
          <button class="quick-action" data-message="Show me today's AI signals">AI Signals</button>
          <button class="quick-action" data-message="Analyze the current market">Market Analysis</button>
          <button class="quick-action" data-message="Help me with trading strategies">Trading Help</button>
        </div>
      </div>
    `;

    // Add quick action button styles
    const style = document.createElement('style');
    style.textContent = `
      .quick-action {
        padding: 6px 12px;
        border: 1px solid rgba(255,255,255,0.3);
        border-radius: 15px;
        background: rgba(255,255,255,0.1);
        color: white;
        cursor: pointer;
        font-size: 0.8em;
        transition: all 0.2s;
      }
      .quick-action:hover {
        background: rgba(255,255,255,0.2);
        transform: translateY(-1px);
      }
      #avatar-text-input::placeholder {
        color: rgba(255,255,255,0.7);
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(textInterface);

    // Add event listeners
    const input = document.getElementById('avatar-text-input') as HTMLInputElement;
    const sendButton = document.getElementById('avatar-text-send') as HTMLButtonElement;
    const quickActions = document.querySelectorAll('.quick-action');

    const sendMessage = () => {
      const message = input.value.trim();
      if (message) {
        this.processUserInput(message);
        input.value = '';
      }
    };

    sendButton.addEventListener('click', sendMessage);
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        sendMessage();
      }
    });

    quickActions.forEach(button => {
      button.addEventListener('click', () => {
        const message = button.getAttribute('data-message');
        if (message) {
          input.value = message;
          sendMessage();
        }
      });
    });
  }

  async startListening(): Promise<void> {
    if (this.isTextMode) {
      console.log('💬 In text mode - use text interface');
      return;
    }

    if (!this.recognition) {
      throw new Error('Speech recognition not initialized');
    }

    if (this.isListening) {
      console.log('Already listening...');
      return;
    }

    try {
      this.recognition.start();
    } catch (error) {
      console.error('Failed to start listening:', error);
      this.initializeTextMode();
    }
  }

  stopListening(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
  }

  private async processUserInput(message: string): Promise<void> {
    console.log('💬 Processing user input:', message);
    this.updateStatus('processing');
    
    try {
      const response = await this.conversationManager.processUserMessage(message);
      this.updateConversationDisplay(message, response);
      
      // Speak response if possible
      if (this.voiceCapabilities.hasSpeechSynthesis) {
        await this.speakResponse(response);
      }
    } catch (error) {
      console.error('Error processing user input:', error);
      const errorResponse = 'I had trouble processing that. Could you try again?';
      this.updateConversationDisplay(message, errorResponse);
    }
    
    this.updateStatus(this.isTextMode ? 'text-mode' : 'listening');
  }

  private updateConversationDisplay(userMessage: string, aiResponse: string): void {
    // Create or update conversation display
    let conversationDiv = document.getElementById('avatar-conversation-display');
    if (!conversationDiv) {
      conversationDiv = document.createElement('div');
      conversationDiv.id = 'avatar-conversation-display';
      conversationDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        width: 400px;
        max-height: 300px;
        overflow-y: auto;
        background: rgba(255,255,255,0.95);
        backdrop-filter: blur(10px);
        border-radius: 15px;
        padding: 20px;
        box-shadow-subtle: 0 8px 32px rgba(0,0,0,0.2);
        z-index: 999;
      `;
      document.body.appendChild(conversationDiv);
    }

    // Add new conversation
    const conversationHTML = `
      <div style="margin-bottom: 15px;">
        <div style="
          padding: 10px;
          border-radius: 10px;
          background: #e3f2fd;
          margin-left: 20px;
          margin-bottom: 10px;
        ">
          <div style="font-size: 0.8em; color: #666; margin-bottom: 5px;">
            ${new Date().toLocaleTimeString()} - You
          </div>
          <div style="color: #333;">
            ${userMessage}
          </div>
        </div>
        <div style="
          padding: 10px;
          border-radius: 10px;
          background: #f5f5f5;
          margin-right: 20px;
        ">
          <div style="font-size: 0.8em; color: #666; margin-bottom: 5px;">
            ${new Date().toLocaleTimeString()} - AI
          </div>
          <div style="color: #333;">
            ${aiResponse}
          </div>
        </div>
      </div>
    `;

    conversationDiv.innerHTML += conversationHTML;
    conversationDiv.scrollTop = conversationDiv.scrollHeight;
  }

  private async speakResponse(text: string): Promise<void> {
    if (!window.speechSynthesis || !text) return;

    try {
      speechSynthesis.cancel(); // Cancel any ongoing speech
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 0.8;

      const voices = speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.includes('en') && voice.name.includes('Google')
      );
      if (preferredVoice) utterance.voice = preferredVoice;

      speechSynthesis.speak(utterance);
    } catch (error) {
      console.warn('Text-to-speech failed:', error);
    }
  }

  private updateStatus(status: 'listening' | 'processing' | 'text-mode' | 'ready'): void {
    const statusText = {
      'listening': '🎤 Listening...',
      'processing': '🤔 Thinking...',
      'text-mode': '💬 Chat Mode Active',
      'ready': '✅ Ready'
    };
    
    console.log(`🔄 Avatar status: ${statusText[status] || 'Ready'}`);
    if (this.onStatusChange) {
      this.onStatusChange(statusText[status] || 'Ready');
    }
  }

  isCurrentlyInTextMode(): boolean {
    return this.isTextMode;
  }

  getVoiceCapabilities(): any {
    return this.voiceCapabilities;
  }
} 